import type { GetApiSampleTestsData, SampleTestStatus } from '@/api';
import type { TableSort } from '@/ui';

import { formatSort, isSameSort, parseSort } from '../../../lib/tableSort';

/*
 * Состояние плана тестирования образцов в search params маршрута. По умолчанию -
 * тесты в «Тестируется», ближайшие по дате теста сверху: чистый список выглядит
 * как /sample-tests.
 */

export const PAGE_SIZE = 20;

/** Статус: один из статусов, «Закрытые» (оба итога) или все. */
export type StatusFilter = SampleTestStatus | 'closed' | 'all';

/** Поля, по которым бэкенд разрешает сортировать (SearchSampleTestsQuery.SortFields). */
export const SORT_FIELDS = [
  'testDate',
  'shipmentDate',
  'partnerName',
  'productName',
] as const;

const DEFAULT_SORT: TableSort = { field: 'testDate', dir: 'asc' };
const DEFAULT_STATUS: StatusFilter = 'Testing';

const STATUS_FILTERS: ReadonlyArray<StatusFilter> = [
  'Testing',
  'Succeeded',
  'Failed',
  'closed',
  'all',
];

const CLOSED_STATUSES: Array<SampleTestStatus> = ['Succeeded', 'Failed'];

export interface SampleTestsSearch {
  search?: string;
  status?: StatusFilter;
  /** Только «пора звонить»: тест идёт, дата теста сегодня или прошла. */
  due?: boolean;
  /** Идентификатор партнёра-клиента в Culture. */
  partner?: string;
  /** Группа номенклатуры товара - наименованием целиком. */
  group?: string;
  page?: number;
  sort?: string;
}

export interface ResolvedSampleTestsSearch {
  search: string;
  status: StatusFilter;
  due: boolean;
  partner: string | undefined;
  /** '' - все группы. */
  group: string;
  page: number;
  sort: TableSort;
}

const isStatusFilter = (value: unknown): value is StatusFilter =>
  (STATUS_FILTERS as ReadonlyArray<unknown>).includes(value);

const trimString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

export const validateSampleTestsSearch = (
  raw: Record<string, unknown>,
): SampleTestsSearch => {
  const result: SampleTestsSearch = {};

  const search = trimString(raw.search);
  if (search !== '') result.search = search;

  if (isStatusFilter(raw.status) && raw.status !== DEFAULT_STATUS)
    result.status = raw.status;

  if (raw.due === true || raw.due === 'true') result.due = true;

  const partner = trimString(raw.partner);
  if (partner !== '') result.partner = partner;

  const group = trimString(raw.group);
  if (group !== '') result.group = group;

  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) result.page = page;

  const sort = parseSort(raw.sort, SORT_FIELDS);
  if (sort && !isSameSort(sort, DEFAULT_SORT)) result.sort = formatSort(sort);

  return result;
};

export const resolveSampleTestsSearch = (
  search: SampleTestsSearch,
): ResolvedSampleTestsSearch => ({
  search: search.search ?? '',
  status: search.status ?? DEFAULT_STATUS,
  due: search.due ?? false,
  partner: search.partner,
  group: search.group ?? '',
  page: search.page ?? 1,
  sort: parseSort(search.sort, SORT_FIELDS) ?? DEFAULT_SORT,
});

/** Фильтры отличаются от умолчания - есть что сбрасывать. */
export const hasSampleTestsFilters = (
  resolved: ResolvedSampleTestsSearch,
): boolean =>
  resolved.search !== '' ||
  resolved.status !== DEFAULT_STATUS ||
  resolved.due ||
  resolved.partner !== undefined ||
  resolved.group !== '';

const toStatuses = (
  status: StatusFilter,
): Array<SampleTestStatus> | undefined => {
  if (status === 'all') return undefined;
  if (status === 'closed') return CLOSED_STATUSES;
  return [status];
};

export const toSampleTestsQuery = (
  resolved: ResolvedSampleTestsSearch,
): NonNullable<GetApiSampleTestsData['query']> => {
  const query: NonNullable<GetApiSampleTestsData['query']> = {
    PageNumber: resolved.page,
    PageSize: PAGE_SIZE,
    Sort: formatSort(resolved.sort),
  };

  const statuses = toStatuses(resolved.status);
  if (statuses !== undefined) query.Statuses = statuses;
  if (resolved.due) query.IsDue = true;
  if (resolved.partner !== undefined) query.PartnerId = resolved.partner;
  if (resolved.group !== '') query.ProductGroup = resolved.group;
  if (resolved.search !== '') query.Search = resolved.search;

  return query;
};
